/**
 * AdminA2DryRun.jsx · A2 Orquestado en modo Dry-Run.
 */
import { useState, useRef, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Play, Bot, CheckCircle, XCircle, Loader2, Clock } from "lucide-react";

export default function AdminA2DryRun() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hours, setHours] = useState(8);
  const [limit, setLimit] = useState(30);
  const [maxDays, setMaxDays] = useState(2);
  // v2.8.6 · Background task polling
  const [progress, setProgress] = useState({ phase: "", progress: 0, total: 0, task_id: null });
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runSimulation = async () => {
    setRunning(true);
    setResult(null);
    setSelectedIds(new Set());
    setProgress({ phase: "Iniciando…", progress: 0, total: 0, task_id: null });
    try {
      const { data: kick } = await api.post(`/a2/dry-run?hours=${hours}&limit=${limit}&max_days_per_tech=${maxDays}`);
      const taskId = kick.task_id;
      setProgress(p => ({ ...p, task_id: taskId }));
      // v2.8.6.1 · Primer poll rápido (300ms) para renderizar phase antes del 1500ms interval
      const pollOnce = async () => {
        try {
          const { data: st } = await api.get(`/a2/status/${taskId}`);
          setProgress({
            phase: st.phase || "Procesando…",
            progress: st.progress || 0,
            total: st.total || 0,
            task_id: taskId,
          });
          if (st.status === "done") {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            setResult(st.result);
            setSelectedIds(new Set((st.result?.proposals || []).map(p => p.odoo_task_id)));
            toast.success(`Simulación: ${st.result.scheduled_count} agendadas · ${st.result.rejected_count} desiertas`);
            setRunning(false);
            return true;
          } else if (st.status === "error") {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            toast.error(`Error: ${st.error || "desconocido"}`);
            setRunning(false);
            return true;
          }
        } catch (err) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          toast.error("No se pudo consultar el estado de la simulación");
          setRunning(false);
          return true;
        }
        return false;
      };
      setTimeout(pollOnce, 300);
      // Polling cada 1.5s
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(pollOnce, 1500);
    } catch (e) {
      toast.error("Error al iniciar simulación");
      setRunning(false);
    }
  };

  const toggleId = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (selectedIds.size === (result?.proposals?.length || 0)) setSelectedIds(new Set());
    else setSelectedIds(new Set((result?.proposals || []).map(p => p.odoo_task_id)));
  };

  const applyProposals = async () => {
    if (!result?.proposals?.length) return;
    const selected = result.proposals.filter(p => selectedIds.has(p.odoo_task_id));
    if (!selected.length) { toast.error("Selecciona al menos una propuesta"); return; }
    if (!window.confirm(`¿Aplicar ${selected.length} propuesta(s)? Se escribirán los agendamientos reales en master_ot.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/a2/apply-proposals", { proposals: selected });
      toast.success(`✅ ${data.applied} agendamientos guardados · ${data.failed} fallidos`);
      // Marcar aplicadas en la vista
      setResult(prev => ({
        ...prev,
        proposals: prev.proposals.map(p => data.applied_ots.find(x => x.ot === p.odoo_task_id) ? { ...p, _applied: true } : p),
      }));
      setSelectedIds(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al aplicar"); }
    finally { setApplying(false); }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="a2-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#F3E8FF", border: "1px solid #A855F7" }}>
              <Bot size={11} className="text-purple-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-purple-700">Agente A2 · Motor orquestado · DRY-RUN</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Propuesta de Agendamiento A2</h1>
            <p className="mt-1 text-sm text-gray-500">Simula ciclo completo A6→A3→A5→A1→A4 sin persistir. Nada se guarda hasta que actives el motor autónomo.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 block">Ventana</label>
              <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="px-2 py-1.5 border rounded text-sm" data-testid="hours-sel">
                <option value="4">4h</option><option value="8">8h</option><option value="24">24h</option><option value="72">72h</option><option value="168">7d</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 block">Límite OTs</label>
              <input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="px-2 py-1.5 border rounded text-sm w-20" data-testid="limit-input"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 block" title="Máximo de días del calendario que cada técnico puede recibir en la simulación">Días/téc</label>
              <input type="number" min="1" max="7" value={maxDays} onChange={(e) => setMaxDays(Number(e.target.value))} className="px-2 py-1.5 border rounded text-sm w-16" data-testid="max-days-input"/>
            </div>
            <button onClick={runSimulation} disabled={running} className="neu-btn neu-btn-primary" data-testid="btn-run">
              {running ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>} Simular
            </button>
          </div>
        </div>

        {running && (
          <div className="neu-card p-6" data-testid="a2-progress-card">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 size={16} className="animate-spin text-purple-700" />
              <div className="text-sm font-semibold text-[#0A3747]" data-testid="a2-progress-phase">
                {progress.phase || "Procesando…"}
              </div>
              <div className="ml-auto text-[11px] tabular-nums text-gray-500" data-testid="a2-progress-count">
                {progress.progress} / {progress.total || "?"}
              </div>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300 ease-out"
                style={{
                  width: progress.total > 0 ? `${Math.min(100, (progress.progress / progress.total) * 100)}%` : "8%",
                  background: "linear-gradient(90deg,#A855F7,#7C3AED)",
                }}
                data-testid="a2-progress-bar"
              />
            </div>
            {progress.task_id && (
              <div className="mt-2 text-[10px] text-gray-400 font-mono truncate" data-testid="a2-task-id">
                task_id: {progress.task_id}
              </div>
            )}
          </div>
        )}

        {!result && !running ? (
          <div className="neu-card p-12 text-center">
            <Bot size={40} className="mx-auto text-gray-300 mb-2"/>
            <div className="text-gray-500">Presiona &quot;Simular&quot; para generar la propuesta.</div>
          </div>
        ) : result ? (
          <>
            {/* Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <KPI label="Evaluadas" value={result.total_evaluated} icon={Clock} color="#0A3747"/>
              <KPI label="Agendables" value={result.scheduled_count} icon={CheckCircle} color="#059669"/>
              <KPI label="Desiertas" value={result.rejected_count} icon={XCircle} color="#DC2626"/>
            </div>

            {/* Breakdown razones */}
            {Object.keys(result.reason_breakdown || {}).length > 0 && (
              <div className="neu-card p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Motivos de rechazo</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.reason_breakdown).map(([reason, n]) => (
                    <span key={reason} className="px-2 py-1 rounded-full bg-red-50 text-red-800 text-xs border border-red-200" data-testid={`reason-${n}`}>
                      {reason}: <b>{n}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Propuestas */}
            {result.proposals?.length > 0 && (
              <div className="neu-card overflow-hidden">
                <div className="p-3 border-b bg-emerald-50 flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-bold text-emerald-900">Propuestas de asignación ({result.proposals.length})</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-emerald-900">{selectedIds.size} / {result.proposals.length} seleccionadas</span>
                    <button onClick={toggleAll} className="text-[10px] text-emerald-800 underline" data-testid="toggle-all">
                      {selectedIds.size === result.proposals.length ? "Deseleccionar todas" : "Seleccionar todas"}
                    </button>
                    <button
                      onClick={applyProposals}
                      disabled={applying || selectedIds.size === 0}
                      className="px-3 py-1.5 text-xs font-bold rounded disabled:opacity-40"
                      style={{ background: "linear-gradient(145deg,#34D399,#059669)", color: "white",
                               boxShadow: "-2px -2px 6px rgba(255,255,255,0.5), 2px 2px 6px rgba(5,150,105,0.4)" }}
                      data-testid="apply-proposals-btn"
                    >
                      {applying ? "Aplicando..." : `✓ Aplicar Propuesta A2 (${selectedIds.size})`}
                    </button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="text-[9px] uppercase tracking-widest text-gray-500 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 w-8"></th>
                      <th className="px-2 py-1.5 text-left">OT</th>
                      <th className="px-2 py-1.5 text-center">Score A6</th>
                      <th className="px-2 py-1.5 text-left">Técnico sugerido</th>
                      <th className="px-2 py-1.5 text-left">Bloque (traslado+ejec+regreso)</th>
                      <th className="px-2 py-1.5 text-left">Desglose</th>
                      <th className="px-2 py-1.5 text-left">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.proposals.map(p => (
                      <tr key={p.odoo_task_id} className={`border-b ${p._applied ? "bg-emerald-50/60" : ""}`} data-testid={`prop-${p.odoo_task_id}`}>
                        <td className="px-2 py-1.5 text-center">
                          {p._applied ? (
                            <span className="text-emerald-700 font-bold text-[10px]" title="Ya aplicada">✓</span>
                          ) : (
                            <input type="checkbox" checked={selectedIds.has(p.odoo_task_id)} onChange={() => toggleId(p.odoo_task_id)} data-testid={`chk-${p.odoo_task_id}`} />
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="font-semibold" style={{ color: "#0A3747" }}>{p.code}</div>
                          <div className="text-[10px] text-gray-500">{p.client}</div>
                        </td>
                        <td className="px-2 py-1.5 text-center font-bold">{p.score.toFixed(1)}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{p.tech_name}</div>
                          <div className="text-[10px] text-gray-500">{p.warehouse}</div>
                        </td>
                        <td className="px-2 py-1.5 text-[10px] tabular-nums" data-testid={`slot-${p.odoo_task_id}`}>
                          <div><b>{new Date(p.slot_start).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}</b></div>
                          <div className="text-gray-500">→ {new Date(p.slot_end).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })} (regreso)</div>
                          {p.exec_start && (
                            <div className="text-[9px] text-emerald-700 mt-0.5">
                              ⚙ {new Date(p.exec_start).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })}–{new Date(p.exec_end).toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-[10px] text-gray-700" data-testid={`breakdown-${p.odoo_task_id}`}>
                          {p.travel_in_min != null ? (
                            <>
                              <div>🚗 {p.travel_in_min}min ida · {p.travel_out_min}min regreso</div>
                              <div>⏱️ {p.exec_min}min ejecución</div>
                              {p.distance_km != null && <div className="text-gray-400">{p.distance_km} km</div>}
                            </>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-[10px] text-gray-600">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Rechazos */}
            {result.rejected?.length > 0 && (
              <div className="neu-card overflow-hidden">
                <div className="p-3 border-b bg-red-50 font-bold text-red-900">Desiertas ({result.rejected.length})</div>
                <table className="w-full text-xs">
                  <thead className="text-[9px] uppercase tracking-widest text-gray-500 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left">OT</th>
                      <th className="px-2 py-1.5 text-left">Cliente</th>
                      <th className="px-2 py-1.5 text-left">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rejected.map(r => (
                      <tr key={r.odoo_task_id} className="border-b" data-testid={`rej-${r.odoo_task_id}`}>
                        <td className="px-2 py-1.5 font-semibold text-[#0A3747]">{r.code}</td>
                        <td className="px-2 py-1.5 text-[10px]">{r.client}</td>
                        <td className="px-2 py-1.5 text-red-700 text-[11px]">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  return (
    <div className="neu-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
        <Icon size={12}/> {label}
      </div>
      <div className="text-3xl font-bold mt-1 tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}
